import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from './general/backend.service';
import { ToastService } from './general/toast.service';
import { TranslateService } from '@ngx-translate/core';
import {
  GradeRevision,
  GradeDocument,
  CreateGradeRevisionRequest,
  UpdateGradeRevisionRequest,
  CreateGradeDocumentRequest,
  UpdateGradeDocumentRequest,
  GradeDocumentFilter,
  CreateGradeRevisionWithAIRequest,
  CreateRevisionFromSearchRequest,
  GradeTable,
  GradeTableToDocumentMap,
  GradeQuality,
  PicostPreset
} from '../models/grade.models';

@Injectable({
  providedIn: 'root',
})
export class GradeService {
  private gradeRevisionsSubject = new BehaviorSubject<GradeRevision[]>([]);
  public gradeRevisions$ = this.gradeRevisionsSubject.asObservable();

  private currentRevisionSubject = new BehaviorSubject<GradeRevision | null>(
    null,
  );
  public currentRevision$ = this.currentRevisionSubject.asObservable();

  constructor(
    private backend: BackendService,
    private toast: ToastService,
    private translateService: TranslateService,
  ) {}

  // Grade Revision Management
  getRevisions(): Observable<GradeRevision[]> {
    return new Observable((subscriber) => {
      this.backend.get('grade/revisions').subscribe(
        (response: any) => {
          // Handle different response formats from backend (including Laravel pagination)
          let revisions: GradeRevision[];
          if (response && response.data && Array.isArray(response.data)) {
            // Laravel paginated response
            revisions = response.data;
          } else if (Array.isArray(response)) {
            // Simple array response
            revisions = response;
          } else if (response && Array.isArray(response.revisions)) {
            // Custom response format
            revisions = response.revisions;
          } else {
            revisions = [];
            console.log(' Unrecognized response format, using empty array');
          }

          this.gradeRevisionsSubject.next(revisions);
          subscriber.next(revisions);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  getRevision(id: number): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.get(`grade/revisions/${id}`).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          this.currentRevisionSubject.next(revision);
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  createRevision(
    request: CreateGradeRevisionRequest,
  ): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.post('grade/revisions', request).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          this.toast.success(
            this.translateService.instant('grade.revision.created.title'),
            this.translateService.instant('grade.revision.created.message'),
          );
          this.refreshRevisions();
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  updateRevision(
    id: number,
    request: UpdateGradeRevisionRequest,
  ): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.put(`grade/revisions/${id}`, request).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          this.toast.success(
            this.translateService.instant('grade.revision.updated.title'),
            this.translateService.instant('grade.revision.updated.message'),
          );
          this.currentRevisionSubject.next(revision);
          this.refreshRevisions();
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  deleteRevision(id: number): Observable<void> {
    return new Observable((subscriber) => {
      this.backend.destroy(`grade/revisions/${id}`).subscribe(
        () => {
          this.toast.success(
            this.translateService.instant('grade.revision.deleted.title'),
            this.translateService.instant('grade.revision.deleted.message'),
          );
          this.refreshRevisions();
          if (this.currentRevisionSubject.value?.id === id) {
            this.currentRevisionSubject.next(null);
          }
          subscriber.next();
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  duplicateRevision(id: number): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.post(`grade/revisions/${id}/duplicate`, {}).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          this.toast.success(
            this.translateService.instant('grade.revision.duplicated.title'),
            this.translateService.instant('grade.revision.duplicated.message'),
          );
          this.refreshRevisions();
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  createRevisionFromSearch(
    request: CreateRevisionFromSearchRequest,
  ): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.post('grade/revisions/from-search', request).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          this.toast.success(
            this.translateService.instant(
              'grade.revision.created_from_search.title',
            ),
            this.translateService.instant(
              'grade.revision.created_from_search.message',
            ),
          );
          this.refreshRevisions();
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  // Document Management
  getDocuments(filter?: GradeDocumentFilter): Observable<GradeDocument[]> {
    return new Observable((subscriber) => {
      const endpoint = 'grade/documents';
      let url = endpoint;
      if (filter) {
        const params = new URLSearchParams();
        Object.keys(filter).forEach((key) => {
          const value = filter[key as keyof GradeDocumentFilter];
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url = `${endpoint}?${queryString}`;
        }
      }

      this.backend.get(url).subscribe(
        (response: any) => {
          // Handle paginated Laravel response structure
          let documents: GradeDocument[];
          if (response && response.data && Array.isArray(response.data)) {
            // Laravel paginated response
            documents = response.data as GradeDocument[];
          } else if (Array.isArray(response)) {
            // Simple array response
            documents = response as GradeDocument[];
          } else {
            // Fallback to empty array
            documents = [];
          }

          subscriber.next(documents);
          subscriber.complete();
        },
        (error) => {
          console.error(' Error details:', error.message, error.status);
          subscriber.error(error);
        },
      );
    });
  }

  createDocument(
    request: CreateGradeDocumentRequest,
  ): Observable<GradeDocument> {
    return new Observable((subscriber) => {
      this.backend.post('grade/documents', request).subscribe(
        (response: any) => {
          const document = response as GradeDocument;
          this.toast.success(
            this.translateService.instant('grade.document.created.title'),
            this.translateService.instant('grade.document.created.message'),
          );
          subscriber.next(document);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  updateDocument(
    id: number,
    request: UpdateGradeDocumentRequest,
  ): Observable<GradeDocument> {
    return new Observable((subscriber) => {
      this.backend.put(`grade/documents/${id}`, request).subscribe(
        (response: any) => {
          const document = response as GradeDocument;
          this.toast.success(
            this.translateService.instant('grade.document.updated.title'),
            this.translateService.instant('grade.document.updated.message'),
          );
          subscriber.next(document);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  deleteDocument(id: number): Observable<void> {
    return new Observable((subscriber) => {
      this.backend.destroy(`grade/documents/${id}`).subscribe(
        () => {
          this.toast.success(
            this.translateService.instant('grade.document.deleted.title'),
            this.translateService.instant('grade.document.deleted.message'),
          );
          subscriber.next();
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  // PDF Operations
  uploadPDF(documentId: number, file: File): Observable<GradeDocument> {
    return new Observable((subscriber) => {
      const formData = new FormData();
      formData.append('pdf', file);

      this.backend
        .postFormData(`grade/documents/${documentId}/upload-pdf`, formData)
        .subscribe(
          (response: any) => {
            const document = response as GradeDocument;
            this.toast.success(
              this.translateService.instant(
                'grade.document.pdf_uploaded.title',
              ),
              this.translateService.instant(
                'grade.document.pdf_uploaded.message',
              ),
            );
            subscriber.next(document);
            subscriber.complete();
          },
          (error) => subscriber.error(error),
        );
    });
  }

  downloadPDF(documentId: number): Observable<Blob> {
    return new Observable((subscriber) => {
      this.backend
        .getBlob(`grade/documents/${documentId}/download-pdf`)
        .subscribe(
          (response: any) => {
            const blob = response as Blob;
            subscriber.next(blob);
            subscriber.complete();
          },
          (error) => subscriber.error(error),
        );
    });
  }

  extractFromPDF(documentId: number): Observable<GradeDocument> {
    return new Observable((subscriber) => {
      this.backend
        .post(`grade/documents/${documentId}/extract-from-pdf`, {})
        .subscribe(
          (response: any) => {
            const document = response as GradeDocument;
            this.toast.success(
              this.translateService.instant(
                'grade.document.pdf_extracted.title',
              ),
              this.translateService.instant(
                'grade.document.pdf_extracted.message',
              ),
            );
            subscriber.next(document);
            subscriber.complete();
          },
          (error) => subscriber.error(error),
        );
    });
  }

  // Enhanced AI Integration
  createRevisionWithAI(
    request: CreateGradeRevisionWithAIRequest,
  ): Observable<{ revision: GradeRevision; process_id: number }> {
    return new Observable((subscriber) => {
      this.backend.post('ai/create-grade-revision-with-ai', request).subscribe(
        (response: any) => {
          const result = response as {
            revision: GradeRevision;
            process_id: number;
          };
          this.refreshRevisions();
          subscriber.next(result);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  getRevisionFromProcess(processId: number): Observable<GradeRevision> {
    return new Observable((subscriber) => {
      this.backend.get(`ai/process/${processId}/grade-revision`).subscribe(
        (response: any) => {
          const revision = response as GradeRevision;
          subscriber.next(revision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  // Migration Utilities
  convertGradeTableToDocument(
    gradeTable: GradeTable,
    gradeRevisionId: number,
    sourceType: 'search' | 'manual_entry' = 'search',
  ): CreateGradeDocumentRequest {
    return {
      grade_revision_id: gradeRevisionId,
      title: gradeTable.articleTitle,
      authors: gradeTable.authors,
      year: gradeTable.year,
      study_type: gradeTable.studyType,
      main_outcome: gradeTable.mainOutcome,
      precision: this.mapStringToGradeQuality(gradeTable.precision),
      consistency: this.mapStringToGradeQuality(gradeTable.consistency),
      risk_of_bias: this.mapStringToRiskOfBias(gradeTable.riskOfBias),
      grade_quality: this.mapStringToGradeQuality(gradeTable.gradeQuality),
      source_type: sourceType,
    };
  }

  convertGradeTableArrayToDocuments(
    gradeTables: GradeTable[],
    gradeRevisionId: number,
    sourceType: 'search' | 'manual_entry' = 'search',
  ): CreateGradeDocumentRequest[] {
    return gradeTables.map((table) =>
      this.convertGradeTableToDocument(table, gradeRevisionId, sourceType),
    );
  }

  // Legacy compatibility method
  saveLegacyGradeAnalysis(
    gradeTables: GradeTable[],
    projectId: number,
    question: string,
    searchQuery: string,
  ): Observable<GradeRevision> {
    const createRequest: CreateRevisionFromSearchRequest = {
      organization_project_id: projectId,
      title: `Grade Analysis - ${new Date().toLocaleDateString()}`,
      description: `Automated grade analysis for: ${question}`,
      search_results: gradeTables,
      pico_question: question,
      keywords: searchQuery, // Include keywords from search query
    };

    return this.createRevisionFromSearch(createRequest);
  }

  // Helper methods
  private refreshRevisions(): void {
    this.getRevisions().subscribe();
  }

  private mapStringToGradeQuality(value: string): GradeQuality {
    const lowerValue = value.toLowerCase().replace(/\s+/g, '_');
    if (['very_low', 'low', 'moderate', 'high'].includes(lowerValue)) {
      return lowerValue as GradeQuality;
    }
    return 'moderate'; // default fallback
  }

  private mapStringToRiskOfBias(
    value: string,
  ): 'very_high' | 'high' | 'moderate' | 'low' {
    const lowerValue = value.toLowerCase().replace(/\s+/g, '_');
    if (['very_high', 'high', 'moderate', 'low'].includes(lowerValue)) {
      return lowerValue as 'very_high' | 'high' | 'moderate' | 'low';
    }
    return 'moderate'; // default fallback
  }

  // Utility methods for reactive state management
  getCurrentRevision(): GradeRevision | null {
    return this.currentRevisionSubject.value;
  }

  setCurrentRevision(revision: GradeRevision | null): void {
    this.currentRevisionSubject.next(revision);
  }

  getGradeRevisions(): GradeRevision[] {
    return this.gradeRevisionsSubject.value;
  }

  // Project integration helper
  getRevisionsByProjectId(projectId: number): Observable<GradeRevision[]> {
    return new Observable((subscriber) => {
      // Direct API call for project-specific revisions to avoid caching issues

      this.backend.get(`grade/revisions?project_id=${projectId}`).subscribe(
        (response: any) => {
          // Handle different response formats from backend (including Laravel pagination)
          let revisions: GradeRevision[];
          if (response && response.data && Array.isArray(response.data)) {
            // Laravel paginated response
            revisions = response.data;
          } else if (Array.isArray(response)) {
            // Simple array response
            revisions = response;
          } else if (response && Array.isArray(response.revisions)) {
            // Custom response format
            revisions = response.revisions;
          } else {
            revisions = [];
            console.log(' Unrecognized response format, using empty array');
          }

          // Filter by project ID as additional safety measure
          // Handle both string and number types for project ID comparison
          const projectRevisions = revisions.filter(
            (revision) =>
              revision.organization_project_id == projectId ||
              Number(revision.organization_project_id) === Number(projectId),
          );

          subscriber.next(projectRevisions);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  // ============================================================================
  // PICOST presets (Statement Generator)
  // Past PICOST generations are stored in the existing grade_revisions table,
  // tagged with search_parameters.type = 'picost_preset'. This lets the user
  // save the PICOST framework + the question/info that generated it + keywords,
  // and reload it later into the Statement Generator form.
  // ============================================================================

  private static readonly PICOST_PRESET_TYPE = 'picost_preset';

  /**
   * List the saved PICOST presets for a project (most recent first).
   * Reuses the standard grade/revisions listing and filters to the rows
   * tagged as PICOST presets for this project.
   */
  getPicostPresets(projectId: number): Observable<GradeRevision[]> {
    return new Observable((subscriber) => {
      this.backend
        .get(
          `grade/revisions?organization_project_id=${projectId}&per_page=200`,
        )
        .subscribe(
          (response: any) => {
            let all: GradeRevision[];
            if (response && Array.isArray(response.data)) {
              all = response.data;
            } else if (Array.isArray(response)) {
              all = response;
            } else if (response && Array.isArray(response.revisions)) {
              all = response.revisions;
            } else {
              all = [];
            }

            const presets = all
              .filter(
                (r) =>
                  r?.search_parameters?.type ===
                  GradeService.PICOST_PRESET_TYPE,
              )
              .filter(
                (r) => Number(r.organization_project_id) === Number(projectId),
              )
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              );

            subscriber.next(presets);
            subscriber.complete();
          },
          (error) => subscriber.error(error),
        );
    });
  }

  /**
   * Save the current PICOST framework + keywords as a new preset.
   */
  savePicostPreset(request: {
    title: string;
    organization_project_id: number;
    organization_id?: number;
    picost: PicostPreset;
    keywords?: string;
    search_query?: string;
    description?: string;
    pubmed_query?: string;
    pubmed_strategy?: any;
  }): Observable<GradeRevision> {
    const payload: any = {
      title: request.title,
      description: request.description,
      organization_project_id: request.organization_project_id,
      organization_id: request.organization_id,
      search_query: request.search_query,
      keywords: request.keywords,
      search_parameters: {
        type: GradeService.PICOST_PRESET_TYPE,
        picost: request.picost,
        pubmed_query: request.pubmed_query,
        pubmed_strategy: request.pubmed_strategy,
      },
      status: 'draft',
    };

    return new Observable((subscriber) => {
      this.backend.post('grade/revisions', payload).subscribe(
        (response: any) => {
          subscriber.next(response as GradeRevision);
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  /**
   * Delete a saved PICOST preset.
   */
  deletePicostPreset(id: number): Observable<void> {
    return new Observable((subscriber) => {
      this.backend.destroy(`grade/revisions/${id}`).subscribe(
        () => {
          subscriber.next();
          subscriber.complete();
        },
        (error) => subscriber.error(error),
      );
    });
  }

  // PICO question change handler
  handlePicoQuestionChange(
    projectId: number,
    newQuestion: string,
    newSearchQuery: string,
    searchParameters: any,
  ): Observable<GradeRevision> {
    const createRequest: CreateGradeRevisionRequest = {
      title: `PICO Analysis - ${new Date().toLocaleDateString()}`,
      description: `New PICO question analysis: ${newQuestion}`,
      organization_project_id: projectId,
      search_query: newSearchQuery,
      search_parameters: searchParameters,
      pico_question: newQuestion, // Store the PICO question in the dedicated field
      keywords: newSearchQuery, // Store keywords in the dedicated field
      status: 'draft',
    };

    return this.createRevision(createRequest);
  }

  /**
   * Process a single document through AI to populate metadata and analysis
   * @param gradeRevisionId The ID of the grade revision to attach document to
   * @param url The URL of the PDF file or PubMed paper
   * @param type The type of document (PDF or PUBMED)
   * @param picoSearchQuestion The PICO search question for analysis context
   * @returns Observable with the processed document
   */
  processSingleDocument(
    gradeRevisionId: number,
    url: string,
    type: 'PDF' | 'PUBMED',
    picoSearchQuestion: string,
  ): Observable<GradeDocument> {
    const requestPayload: any = {
      gradeRevisionId: gradeRevisionId,
      picoSearchQuestion: picoSearchQuestion,
    };

    // Add the appropriate URL parameter based on document type
    if (type === 'PDF') {
      requestPayload.pdf_url = url;
      requestPayload.pubmed_url = null;
    } else {
      requestPayload.pdf_url = null;
      requestPayload.pubmed_url = url;
    }

    return new Observable((subscriber) => {
      this.backend
        .post('grade/documents/process-single-document', requestPayload)
        .subscribe(
          (response: any) => {
            const processedDocument = response as GradeDocument;

            this.toast.success(
              this.translateService.instant('grade.document.processed.title') ||
                'Document Processed',
              this.translateService.instant(
                'grade.document.processed.message',
              ) || 'Document has been analyzed and updated with AI metadata.',
            );

            subscriber.next(processedDocument);
            subscriber.complete();
          },
          (error) => {
            console.error(' Document processing error:', error);
            this.toast.error(
              this.translateService.instant(
                'grade.document.processing.error.title',
              ) || 'Processing Failed',
              this.translateService.instant(
                'grade.document.processing.error.message',
              ) || 'Failed to process document with AI analysis.',
            );
            subscriber.error(error);
          },
        );
    });
  }
}
